import { boot, createEraroError, eraroCodesMap } from '@dazn/bootstrap-lib';

import { fetchStartupData } from './fetchStartupData';

/**
 * Array list of status considered as portability enabled
 */
const portabilityStatuses = [
    'PortabilityAvailable',
    'NativeAndPortabilityAvailable',
];

export interface StartupModuleParams {
    getGuid: () => string;
    sessionId: string;
    environment: boot.config.Environment;
}

// @todo we are able to guess quite good defaults for all the methods
/**
 * Retrieves startup data (from startUp service)
 */
export const createStartupModule = ({
    getGuid,
    sessionId,
    environment,
}: StartupModuleParams) => {
    let campaignId: string;
    let promoId: string;
    let startupData: boot.startupData.StartupData | undefined;

    type AssetrSD = (
        startupData: boot.startupData.StartupData | undefined,
        method: string,
    ) => asserts startupData is boot.startupData.StartupData;
    const assertStartupData: AssetrSD = (startupData, method) => {
        if (!startupData) {
            throw createEraroError(
                `[Bootstrap] Startup data is not available: ${method}()`,
                eraroCodesMap.category.StartupService,
                eraroCodesMap.code.GenericError,
                eraroCodesMap.internal.GenericError,
            );
        }
    };

    return {
        getCampaignId: () => campaignId,
        setCampaignId: (value: string) => {
            campaignId = value;
        },
        getPromoId: () => promoId,
        setPromoId: (value: string) => {
            promoId = value;
        },
        hasStartupData: () => Boolean(startupData),
        getStartupData: () => {
            assertStartupData(startupData, 'getStartupData');
            return startupData;
        },
        getAbTests: () => startupData?.AB_Test_Multi,
        setStartupData: (value: boot.startupData.StartupData) => {
            startupData = value;
        },
        getStartupDataCountry: () => {
            assertStartupData(startupData, 'getStartupDataCountry');
            return startupData.Region.Country;
        },
        getRegionLanguage: () =>
            // TODO: assertStartupData(startupData);
            startupData?.Region.Language,
        getApiUrl: (service = '', versions = 'v1') => {
            assertStartupData(startupData, 'getApiUrl');
            return startupData.ServiceDictionary?.[service]?.Versions[versions]
                ?.ServicePath;
        },
        getAllowedLanguages: () => startupData?.SupportedLanguages,
        isPortable: () => {
            const { CountryPortabilityStatus } = startupData?.Region || {};
            return portabilityStatuses.some(
                s => s === CountryPortabilityStatus,
            );
        },
        // in future this method probably should not be exposed
        checkRegionAvailability: () => {
            assertStartupData(startupData, 'checkRegionAvailability');
            return Boolean(startupData.Region?.isAllowed);
        },

        getLocale: () => {
            if (!startupData) return '';
            const { Country, Language } = startupData.Region;

            return `${Language}-${Country.toUpperCase()}`;
        },

        run: ({
            shouldUseMarcoPolo,
            customHeaders,
            languages,
        }: {
            shouldUseMarcoPolo: boolean;
            customHeaders?: {
                isCustomHeadersEnabled?: boolean;
                brandName?: string;
                daznId?: string;
            };
            languages?: string;
        }) =>
            Promise.resolve()
                .then(() =>
                    fetchStartupData(
                        environment,
                        shouldUseMarcoPolo,
                        getGuid(),
                        sessionId,
                        customHeaders,
                        languages,
                    ),
                )
                .then(response => {
                    window.newrelic?.setCustomAttribute(
                        'geoCountry',
                        response.Region.Country,
                    );

                    window.newrelic?.setCustomAttribute(
                        'clientIP',
                        response.ClientIp ?? '',
                    );

                    // Should we test for correctness?
                    startupData = response;
                }),
    };
};
